import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Webhook as WebhookModel } from '../../models';
import { AnchorNav, Button, ButtonDropdown, Icon, WeezHeader, SplitContainer } from '@weezevent/nacre';
import { CommentsBlock } from '@weezevent/gossip-front';

import { WebHookActions, WebhookHeader, WebHookModal, WebHookPayloadModal } from './components';
import { usePermissions } from '../../models/user/permissions';
import { PendingCalls } from './components/PendingCalls';
import useWebHookActions from './hooks/useWebHookActions';
import { useAuth } from 'react-oidc-context';

export const Webhook = ({ id, organizationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const user = usePermissions();
    const auth = useAuth();

    const userId = `user_${auth?.user.profile.sub}`;
    const [openModal, setOpenModal] = React.useState(false);
    const [payloadModal, setPayloadModal] = React.useState({ open: false, call: null });

    const [webhook, loadingWebhook, syncWebhook] = WebhookModel.useApiModel(
        {
            id,
            organizationId,
            strict: true,
            cache: false,
            allow_cache: false
        },
        [id, organizationId]
    );

    const webhookActions = useWebHookActions(webhook);

    const backLink = React.useMemo(() => {
        if (!organizationId) {
            return '/tools/webhooks';
        }
        return `/organizations/O${organizationId}/tools/webhooks`;
    }, [organizationId]);

    const handleAction = React.useCallback(
        (_, action) => {
            switch (action) {
                case 'toggle':
                    webhook.toggle({ organizationId }).then(() => syncWebhook());
                    break;
                case 'archive':
                    webhook.delete({ organizationId }).then(() => history.push(backLink));
                    break;
                case 'pause':
                    webhook.pause({ organizationId }).then(() => syncWebhook());
                    break;
                case 'resume':
                    webhook.resume({ organizationId }).then(() => syncWebhook());
                    break;
                default:
                    break;
            }
        },
        [organizationId, syncWebhook, backLink]
    );

    return (
        <>
            <WeezHeader
                rightComponent={[
                    user.canEditWebhooks() && (
                        <ButtonDropdown
                            key="dropdown"
                            disabled={!webhook || loadingWebhook}
                            trigger={<Button key={1} inverted icon={<Icon name={'chevron-bottom'} />} label={t('common.actions')} />}
                            items={<WebHookActions webhook={webhook} handleAction={handleAction} actions={webhookActions} />}
                        />
                    )
                ]}
                title={webhook?.target || ' '}
                backLink={{
                    onClick: () => {
                        history.push(backLink);
                    }
                }}
            />
            <AnchorNav
                items={[
                    {
                        id: 'webhook_profile',
                        title: t('common.profile')
                    },
                    {
                        id: 'webhook_logs',
                        title: t('common.logs')
                    }
                ]}
                rootId="root"
            >
                <SplitContainer additional={<CommentsBlock reference={`weez:webhook:${id}`} userId={userId} />}>
                    {openModal && <WebHookModal webhook={webhook} organizationId={organizationId} onClose={() => setOpenModal(false)} sync={syncWebhook} />}
                    {payloadModal.open && <WebHookPayloadModal call={payloadModal.call} onClose={() => setPayloadModal({ open: false, call: null })} />}
                    <WebhookHeader webhook={webhook} organizationId={organizationId} setOpenModal={setOpenModal} loading={loadingWebhook || !webhook} />
                    <PendingCalls webhook={webhook} organizationId={organizationId} setOpenModal={setPayloadModal} syncWebhook={syncWebhook} />
                </SplitContainer>
            </AnchorNav>
        </>
    );
};
